import { createRequest } from '@hixme/redux-gateway'
import { callWaiter } from 'redux-waiter'
import axios from 'axios'
import { getEpsBaseUrl } from 'modules/api/eps-base-url'
import { preparePersonForEpsPost } from 'modules/api/preparePersonForEpsPost'
import { LocationData, PersonWithAddress } from './types'

/**
 * Get locations for multiple zip codes in a single batch call
 * @param zipCodes - Array of zip codes to lookup
 * @param year - Benefit year (defaults to current year + 1 for enrollment)
 * @returns Array of location data
 */
export const getLocationsBatch = (zipCodes: string[], year: number = new Date().getFullYear() + 1) => (dispatch: any) => {
  const uniqueZipCodes = Array.from(new Set(zipCodes)) // Remove duplicates
  
  // For now, we'll make individual calls to the existing locations API
  // In the future, this could be replaced with a true batch endpoint
  const locationPromises = uniqueZipCodes.map(zipCode => {
    console.log(`Looking up location for zip code: ${zipCode}, year: ${year}`)
    return dispatch(
      createRequest({
        route: 'locationsGet', // Use existing route
        params: {
          year,
          zipCode,
        },
      })
    ).then(
      (data: any): LocationData[] => {
        // Transform API response to our expected format
        // The API returns an array of LocationsCountyStateResponse directly
        if (data && Array.isArray(data)) {
          const transformed = data.map((location: any) => ({
            zipCode: location.ZipCode || zipCode,
            stateProvince: location.StateProvince,
            county: location.County,
            ratingArea: parseInt(location.RatingAreaNumeric, 10),
          }))
          return transformed
        }
        return []
      },
      (error: any) => {
        console.error(`Error getting location for zip code ${zipCode}:`, error)
        return [] // Return empty array for failed requests
      }
    )
  })

  return Promise.all(locationPromises).then((results: LocationData[][]) => {
    // Flatten the results array
    const flattened = results.flat()
    console.log(`Total locations retrieved: ${flattened.length} for ${uniqueZipCodes.length} zip codes`)
    return flattened
  })
}

/**
 * Update a person's rating area
 * @param person - Person object to update
 * @param newRatingArea - New rating area value
 * @returns Promise that resolves to updated person object
 */
export const updatePersonRatingArea = (person: PersonWithAddress, newRatingArea: number) => (dispatch: any) => {
  const updatedPerson = {
    ...person,
    RatingArea: newRatingArea,
  }

  const remapPersonResponse = (data: any) => ({ ...data })

  return new Promise((resolve, reject) => {
    dispatch(
      callWaiter('personPost', {
        requestCreator: () =>
          axios
            .post(`${getEpsBaseUrl()}/core/persons`, preparePersonForEpsPost(updatedPerson))
            .then(({ data }) => remapPersonResponse(data)),
      })
    ).then(
      (response: any) => {
        console.log(`Updated rating area from ${person.RatingArea} to ${newRatingArea} for personId=${person.Id}`)
        resolve(response)
      },
      (error: any) => {
        console.error(`Error updating rating area:`, error)
        reject(error)
      }
    )
  })
}

/**
 * Find matching location for a person's address
 * @param person - Person with address information
 * @param locations - Array of location data from API
 * @returns Matching location or null if not found
 */
export const findMatchingLocation = (person: PersonWithAddress, locations: LocationData[]): LocationData | null => {
  const { PostalCode, StateProvince, County } = person

  // Only find exact match by zip, state, and county
  const matchingLocation = locations.find(
    (location) =>
      location.zipCode === PostalCode &&
      location.stateProvince === StateProvince &&
      location.county === County
  )

  return matchingLocation || null
}
